import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** When true, button is disabled and shows a spinner. */
  isLoading?: boolean;
  children?: ReactNode;
}

const css = `
  .ew-btn-sm {
    padding: 5px 10px;
    font-size: var(--ew-fs-caption);
  }
  .ew-btn-spinner {
    display: inline-block;
    width: 10px;
    height: 10px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: ew-btn-spin 0.7s linear infinite;
  }
  @keyframes ew-btn-spin { to { transform: rotate(360deg); } }
`;

/**
 * Shared button primitive. Wraps the existing `.btn` + `.btn-primary` /
 * `-secondary` / `-ghost` / `-danger` classes from App.tsx so every page
 * renders identical chrome.
 *
 * @example
 *   <Button variant="primary" onClick={save}>Save</Button>
 *   <Button variant="secondary" size="sm" isLoading={mut.isPending}>Cancel</Button>
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled,
  className,
  children,
  type,
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    `btn-${variant}`,
    size === 'sm' ? 'ew-btn-sm' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <style>{css}</style>
      <button
        type={type ?? 'button'}
        className={classes}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...rest}
      >
        {isLoading && <span className="ew-btn-spinner" aria-hidden="true" />}
        {children}
      </button>
    </>
  );
}
